import React, { useState, useRef, useCallback, useEffect } from 'react';
import { TrendingUp, CheckCircle, Send, AlertCircle, MessageCircle } from 'lucide-react';
import { heroData, investmentOptions } from '../../data/mock';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const HERO_BG = "/images/hero_banner.png";
const GOOGLE_APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxhmnsFjMSAt8Ht2_Iux9BbMqP12BTluk7RZzELhlpYz-IWxzqLk7d4LuAQyY-UOmMbEA/exec";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const normalizePhone = (phone) => {
  let digits = phone.replace(/\D/g, '');
  if (digits.startsWith('91') && digits.length === 12) digits = digits.slice(2);
  else if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1);
  return digits;
};

const validatePhone = (phone) => {
  if (phone.length !== 10) return false;
  if (!/^\d+$/.test(phone)) return false;
  if (!['6', '7', '8', '9'].includes(phone[0])) return false;
  return true;
};

const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_content: params.get('utm_content') || '',
    utm_term: params.get('utm_term') || '',
  };
};

const HeroSection = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    city: '',
    investment_interest: '',
    message: '',
    website_url: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [investmentError, setInvestmentError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    if (document.getElementById('turnstile-script')) return;
    const script = document.createElement('script');
    script.id = 'turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        try { window.turnstile.remove(turnstileWidgetId.current); } catch (err) { /* ignore */ }
      }
    };
  }, []);

  const renderTurnstile = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current || !window.turnstile) return;
    if (turnstileWidgetId.current) {
      try { window.turnstile.remove(turnstileWidgetId.current); } catch (err) { /* ignore */ }
    }
    try {
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
        theme: 'light',
        size: 'flexible',
      });
    } catch (e) { console.error('[Form] CAPTCHA error:', e); }
  }, []);

  useEffect(() => {
    if (submitStatus === null && TURNSTILE_SITE_KEY) {
      const timer = setTimeout(() => {
        if (window.turnstile) renderTurnstile();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [submitStatus, renderTurnstile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'phone') setPhoneError('');
    if (name === 'email') setEmailError('');
    if (name === 'full_name') setNameError('');
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, investment_interest: value }));
    setInvestmentError('');
  };

  const resetForm = () => {
    setFormData({ full_name: '', phone: '', email: '', city: '', investment_interest: '', message: '', website_url: '' });
    setTurnstileToken('');
    setPhoneError('');
    setEmailError('');
    setNameError('');
    setInvestmentError('');
    if (window.turnstile && turnstileWidgetId.current) {
      try { window.turnstile.reset(turnstileWidgetId.current); } catch (err) { /* ignore */ }
    }
  };

  const validateForm = () => {
    let isValid = true;
    
    // Name validation
    if (!formData.full_name.trim() || formData.full_name.trim().length < 2) {
      setNameError('Name is required (minimum 2 characters)');
      isValid = false;
    }
    
    // Phone validation - must be exactly 10 digits
    const phoneNormalized = normalizePhone(formData.phone);
    if (!validatePhone(phoneNormalized)) {
      setPhoneError('Phone must be 10 digits starting with 6, 7, 8, or 9');
      isValid = false;
    }
    
    // Email validation - proper format required
    if (!formData.email.trim()) {
      setEmailError('Email is required');
      isValid = false;
    } else {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.email.trim())) {
        setEmailError('Please enter a valid email address');
        isValid = false;
      }
    }
    
    // Investment range validation
    if (!formData.investment_interest) {
      setInvestmentError('Please select investment range');
      isValid = false;
    }
    
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validateForm()) return;
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setErrorMessage('Please complete the security check');
      setSubmitStatus('error');
      return;
    }
    setIsSubmitting(true);
    setErrorMessage('');
    setSubmitStatus(null);
    
    const phoneNormalized = normalizePhone(formData.phone);
    
    // Build URL-encoded payload for Google Apps Script
    const params = new URLSearchParams();
    params.append('fullName', formData.full_name.trim());
    params.append('phone', phoneNormalized);
    params.append('email', formData.email.trim());
    params.append('investmentRange', formData.investment_interest);
    params.append('city', formData.city.trim());
    params.append('source', 'apnafarm.net');
    
    try {
      const response = await fetch(GOOGLE_APPS_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });
      
      // Google Apps Script returns opaque response with no-cors
      // If no error is thrown, consider it successful
      setSubmitStatus('success');
      resetForm();
    } catch (error) {
      let errorMsg = 'Unable to submit your enquiry. Please try again or call us at +91 6292050222.';
      setErrorMessage(errorMsg);
      setSubmitStatus('error');
      if (window.turnstile && turnstileWidgetId.current) {
        try { window.turnstile.reset(turnstileWidgetId.current); setTurnstileToken(''); } catch (err) { /* ignore */ }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewEnquiry = () => {
    setSubmitStatus(null);
    setErrorMessage('');
    resetForm();
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Vibrant Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${HERO_BG}')` }}
      />
      
      {/* Overlay - layered scrim keeps text AA-legible over any part of the photo */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#00291f]/90 via-[#004534]/70 to-[#004534]/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/10" />

      <div className="container mx-auto px-4 lg:px-8 relative z-10 py-20 md:py-24 max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6 md:space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-4 py-2 rounded-full border border-white/25">
              <span className="w-2 h-2 bg-[#c4e538] rounded-full animate-pulse" />
              <span className="text-white text-sm font-medium">Now accepting lease applications</span>
            </div>

            {/* HERO HEADLINE */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.95]">
              <span className="block">{heroData.headline1}</span>
              <span className="block text-[#c4e538] drop-shadow-lg">{heroData.headline2}</span>
            </h1>

            {/* Subheadline */}
            <p className="text-base sm:text-lg md:text-xl text-white/95 font-medium">
              {heroData.subheadline}
            </p>

            {/* Stats Row */}
            <div className="flex flex-wrap items-stretch gap-4 md:gap-7">
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-[#c4e538]" />
                  <p className="text-3xl sm:text-4xl md:text-5xl font-black text-[#c4e538] leading-none">2.5X</p>
                </div>
                <p className="text-white/90 text-sm font-semibold">Minimum returns</p>
                <p className="text-white/60 text-xs">on your lease value</p>
              </div>
              <div className="w-px self-stretch bg-white/20 hidden sm:block" />
              <div>
                <p className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-none mb-1">30</p>
                <p className="text-white/90 text-sm font-semibold">Month term</p>
                <p className="text-white/60 text-xs">fixed at inception</p>
              </div>
              <div className="w-px self-stretch bg-white/20 hidden sm:block" />
              <div>
                <p className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-none mb-1">₹10K</p>
                <p className="text-white/90 text-sm font-semibold">Per cottah</p>
                <p className="text-white/60 text-xs">entry lease price</p>
              </div>
            </div>

            {/* WhatsApp Contact Option */}
            <div className="mt-4">
              <a 
                href="https://wa.me/916292050222?text=Hi%2C%20I%27m%20interested%20in%20leasing%20farmland%20through%20ApnaFarm.%20I%20would%20like%20to%20know%20more%20about%3A%0A%0A1.%20Available%20land%20options%0A2.%20Lease%20terms%20and%20pricing%0A3.%20Expected%20returns%0A%0APlease%20share%20more%20details.%20Thank%20you!"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold px-5 py-3 rounded-full transition-all hover:scale-105 shadow-lg"
              >
                <MessageCircle className="w-5 h-5" />
                <span>Chat on WhatsApp</span>
              </a>
              <p className="text-white/70 text-xs mt-2">Quick enquiry via WhatsApp</p>
            </div>
          </div>

          {/* Right - Enquiry Form */}
          <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-5 md:p-6">
              {submitStatus === 'success' ? (
                <div className="text-center py-4">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-[#004534] mb-2">Thank You!</h3>
                  <p className="text-gray-500 text-sm mb-2">We have received your enquiry.</p>
                  <p className="text-gray-500 text-sm mb-4">Our team will contact you within 24 hours.</p>
                  <p className="text-[#004534] font-semibold text-sm mb-4">
                    Call us: <a href="tel:+916292050222" className="text-[#0C6951] underline">+91 6292050222</a>
                  </p>
                  <Button onClick={handleNewEnquiry} variant="outline" className="rounded-full border-[#004534] text-[#004534] px-5 py-2 text-sm">
                    Submit Another
                  </Button>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <h2 className="text-xl font-bold text-[#004534] mb-1">Get Started Today</h2>
                    <p className="text-gray-500 text-sm">Fill in your details and we will reach out.</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    {submitStatus === 'error' && errorMessage && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-2 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-red-700 text-sm">{errorMessage}</p>
                      </div>
                    )}

                    <div className="absolute left-[-9999px]" aria-hidden="true">
                      <input type="text" name="website_url" value={formData.website_url} onChange={handleInputChange} tabIndex={-1} autoComplete="off" />
                    </div>

                    {/* Name */}
                    <div>
                      <label className="block text-sm font-semibold text-[#004534] mb-1">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter your full name"
                        className={`rounded-lg h-10 text-sm ${nameError ? 'border-red-400' : 'border-gray-200'}`}
                        disabled={isSubmitting}
                      />
                      {nameError && <p className="text-red-500 text-xs mt-1">{nameError}</p>}
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-semibold text-[#004534] mb-1">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="+91 98765 43210"
                        className={`rounded-lg h-10 text-sm ${phoneError ? 'border-red-400' : 'border-gray-200'}`}
                        disabled={isSubmitting}
                      />
                      {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-[#004534] mb-1">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your@email.com"
                        className={`rounded-lg h-10 text-sm ${emailError ? 'border-red-400' : 'border-gray-200'}`}
                        disabled={isSubmitting}
                      />
                      {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
                    </div>

                    {/* Investment Range - FIXED: Same text size as other inputs */}
                    <div>
                      <label className="block text-sm font-semibold text-[#004534] mb-1">
                        Investment Range <span className="text-red-500">*</span>
                      </label>
                      <Select onValueChange={handleSelectChange} value={formData.investment_interest} disabled={isSubmitting}>
                        <SelectTrigger className={`rounded-lg h-10 text-sm ${investmentError ? 'border-red-400' : 'border-gray-200'}`}>
                          <SelectValue placeholder="Select range" className="text-sm" />
                        </SelectTrigger>
                        <SelectContent className="rounded-lg">
                          {investmentOptions.map((option, index) => (
                            <SelectItem key={index} value={option} className="text-sm">{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {investmentError && <p className="text-red-500 text-xs mt-1">{investmentError}</p>}
                    </div>

                    {/* City - Optional */}
                    <div>
                      <label className="block text-sm font-semibold text-[#004534] mb-1">
                        City <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <Input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Your city"
                        className="rounded-lg border-gray-200 h-10 text-sm"
                        disabled={isSubmitting}
                      />
                    </div>

                    {TURNSTILE_SITE_KEY && <div className="flex justify-center"><div ref={turnstileRef} /></div>}

                    <Button
                      type="submit"
                      disabled={isSubmitting || (TURNSTILE_SITE_KEY && !turnstileToken)}
                      className="w-full bg-[#004534] hover:bg-[#0C6951] text-white font-bold rounded-full h-10 text-sm transition-all hover:scale-[1.02] disabled:opacity-60"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                          Submitting...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Send className="w-4 h-4" /> Submit Enquiry
                        </span>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
